import { ApiCacheTags, baseApiService } from '@qapline/api';
import { config } from 'config';

import {
    TGetCommissionsForInvoicingRequestBody,
    TGetCommissionsForInvoicingResponseBody,
    TGetInvoicingStatusCountsResponseBody,
    TGetInvoicingStatusesRequestBody,
    TGetInvoicingStatusesResponseBody,
    TListInvoiceNumbersRequestBody,
    TListInvoiceNumbersResponseBody,
} from '../types';

/**
 * Admin-only invoicing endpoints. All paths target the new `/v3/invoice-new` namespace
 * and query `CommissionNew` / `InvoiceNew` under the hood. CRUD (list/get/create/update/delete)
 * is still re-exported from `@qapline/core` below.
 */
export const invoicingApi = baseApiService.injectEndpoints({
    endpoints: (builder) => ({
        listInvoiceNumbers: builder.query<TListInvoiceNumbersResponseBody, TListInvoiceNumbersRequestBody>({
            query: ({ search }) => ({
                url: config.api.endpoints.invoice.numbers,
                method: 'GET',
                params: {
                    ...(search ? { search } : {}),
                },
            }),
            providesTags: (_, __, arg) => [{ type: ApiCacheTags.invoice.numbers, id: arg.search }],
        }),
        getInvoicingStatusCounts: builder.query<TGetInvoicingStatusCountsResponseBody, void>({
            query: () => ({
                url: config.api.endpoints.invoice.invoicingStatusCounts,
                method: 'GET',
            }),
            providesTags: [ApiCacheTags.invoice.invoicingStatusCounts],
        }),
        getInvoicingStatuses: builder.query<TGetInvoicingStatusesResponseBody, TGetInvoicingStatusesRequestBody>({
            query: (params) => ({
                url: config.api.endpoints.invoice.invoicingStatuses,
                method: 'GET',
                params,
            }),
            providesTags: (_, __, arg) => [{ type: ApiCacheTags.invoice.invoicingStatuses, id: JSON.stringify(arg) }],
        }),
        getCommissionsForInvoicing: builder.query<
            TGetCommissionsForInvoicingResponseBody,
            TGetCommissionsForInvoicingRequestBody
        >({
            query: (params) => ({
                url: config.api.endpoints.invoice.commissionsForInvoicing,
                method: 'GET',
                params,
            }),
            providesTags: (_, __, arg) => [
                { type: ApiCacheTags.invoice.commissionsForInvoicing, id: JSON.stringify(arg) },
            ],
        }),
    }),
});

export const {
    useListInvoiceNumbersQuery,
    useLazyListInvoiceNumbersQuery,
    useGetInvoicingStatusCountsQuery,
    useLazyGetInvoicingStatusCountsQuery,
    useGetInvoicingStatusesQuery,
    useLazyGetInvoicingStatusesQuery,
    useGetCommissionsForInvoicingQuery,
    useLazyGetCommissionsForInvoicingQuery,
} = invoicingApi;

// Re-export core invoice mutations/queries under the admin namespace so existing
// call sites keep their imports stable.
export {
    useCancelInvoiceMutation,
    useCreateInvoiceMutation,
    useDeleteInvoiceMutation,
    useGetInvoiceQuery,
    useLazyGetInvoiceQuery,
    useLazyListInvoicesQuery,
    useListInvoicesQuery,
    usePreviewInvoicePdfMutation,
    useUpdateInvoiceMutation,
} from '@qapline/core';
